import { extractApiErrorMessage } from '@cartwheel/api'
import {
  type CostEstimate,
  myTasksQueryKey,
  type Task,
  taskByIdQueryKey,
  taskInsuranceVerificationContextOptions,
  taskInsuranceVerificationContextQueryKey,
  tasksQueryKey,
  useTaskResolveVerifyInsuranceMutation,
} from '@cartwheel/api/v1'
import {
  Button,
  CostEstimateCard,
  ErrorView,
  InfoBanner,
  LoadingView,
  SegmentedControl,
  useToast,
} from '@cartwheel/components'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useState } from 'react'
import { CannotSendRadio } from './cannot-send-radio'
import { InsuranceCard } from './insurance-card'
import { InternalBillingNoteField } from './internal-billing-note-field'
import { ResolutionSection } from './resolution-section'
import { AppointmentCard } from './shared/appointment-card'
import { TaskDescription } from './shared/task-workspace-primitives'
import { trackTaskCostEstimateInteraction, trackTaskResourceLink } from './task-analytics'
import { TaskCompletionBanner } from './task-completion-banner'

type ResolutionMethod = 'send_estimate' | 'cannot_send'

export function VerifyInsuranceWorkspace({
  task,
  canWork = true,
  onClose,
}: {
  task: Task
  canWork?: boolean
  onClose: () => void
}) {
  const [resolutionMethod, setResolutionMethod] = useState<ResolutionMethod>('send_estimate')
  const [billingNote, setBillingNote] = useState('')
  const [currentEstimate, setCurrentEstimate] = useState<CostEstimate | null>(null)

  const toast = useToast()
  const queryClient = useQueryClient()

  const { data, isPending, isError } = useQuery(
    taskInsuranceVerificationContextOptions({ path: { task_id: task.id } })
  )

  const resolveMutation = useTaskResolveVerifyInsuranceMutation({
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: myTasksQueryKey() })
      void queryClient.invalidateQueries({ queryKey: tasksQueryKey() })
      void queryClient.invalidateQueries({
        queryKey: taskByIdQueryKey({ path: { id: task.id } }),
      })
      toast.success({ description: 'Task resolved' })
      onClose()
    },
    onError: (error) => {
      toast.error({ description: extractApiErrorMessage(error, 'Failed to resolve task') })
    },
  })

  const handleEstimateChange = useCallback((estimate: CostEstimate | null) => {
    setCurrentEstimate(estimate)
  }, [])

  // Re-fetch the task context after an Apero sync finishes: payer associations
  // are materialized before eligibility, even when the eligibility check fails.
  const handleSyncFinished = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: taskInsuranceVerificationContextQueryKey({ path: { task_id: task.id } }),
    })
  }, [queryClient, task.id])

  const sendableEstimateId =
    currentEstimate?.id && currentEstimate.valid_for_send ? currentEstimate.id : null
  const isOutOfNetworkHold = Boolean(data?.upcoming_hold_out_of_network)
  const credentialingStatusUnknown = data?.upcoming_hold_credentialing_status === 'unknown'

  const handleSendEstimate = () => {
    resolveMutation.mutate({
      path: { task_id: task.id },
      body: {
        outcome: 'estimate_confirmed',
        note: billingNote.trim() || undefined,
        cost_estimate_id: sendableEstimateId ?? undefined,
      },
    })
  }

  const handleMarkCannotSend = () => {
    resolveMutation.mutate({
      path: { task_id: task.id },
      body: { outcome: 'client_unresponsive' },
    })
  }

  if (isPending) {
    return <LoadingView title="Loading task context…" />
  }

  if (isError || !data) {
    return <ErrorView title="Unable to load task context" message="Please close and try again." />
  }

  const context = data
  const isTerminal = task.status === 'completed' || task.status === 'canceled'
  const isPendingResolve = resolveMutation.isPending

  return (
    <div className="space-y-8">
      <TaskDescription task={task} />
      <AppointmentCard startTime={context.appointment?.start_time} />
      <InsuranceCard
        insuranceName={context.insurance?.insurance_name}
        policyHolderName={context.insurance?.policy_holder_name}
        memberId={context.insurance?.member_id}
        aperoUrl={context.apero_url}
        availityUrl={context.availity_url}
        showExternalLinks={canWork}
        onResourceClick={(destination) => trackTaskResourceLink(task, destination)}
      />

      {isTerminal ? (
        <TaskCompletionBanner task={task} />
      ) : canWork ? (
        <>
          {credentialingStatusUnknown ? (
            <InfoBanner
              tone="peach"
              title={
                sendableEstimateId
                  ? 'Payer credentialing unavailable'
                  : 'Review insurance before confirming'
              }
              description={
                sendableEstimateId
                  ? "The client's payer information is not available for the credentialing check. Review the cost estimate details before confirming."
                  : "The client's payer information is not available for the credentialing check. Sync insurance data, or manually create or edit the cost estimate before confirming."
              }
            />
          ) : isOutOfNetworkHold ? (
            <InfoBanner
              tone="peach"
              title="Out-of-network clinician"
              description="The client is scheduled with a clinician who is out-of-network. Instead of sending a cost estimate to the family, completing this task will create a followup task for the care coordination team to re-schedule the client with an in-network clinician."
            />
          ) : null}
          <ResolutionSection>
            <SegmentedControl.Root
              value={resolutionMethod}
              onChange={(next) => setResolutionMethod(next as ResolutionMethod)}
            >
              <SegmentedControl.Option value="send_estimate">
                Confirm cost estimate
              </SegmentedControl.Option>
              <SegmentedControl.Option value="cannot_send">
                Cannot confirm estimate
              </SegmentedControl.Option>
            </SegmentedControl.Root>

            {resolutionMethod === 'send_estimate' ? (
              <>
                <CostEstimateCard
                  clientId={task.resource_id}
                  showSendButton={false}
                  onEstimateChange={handleEstimateChange}
                  onSyncFinished={handleSyncFinished}
                  onInteraction={(interaction) =>
                    trackTaskCostEstimateInteraction(task, interaction)
                  }
                />
                <InternalBillingNoteField value={billingNote} onChange={setBillingNote} />
                <Button
                  className="w-full"
                  disabled={isPendingResolve || (!isOutOfNetworkHold && !sendableEstimateId)}
                  onClick={handleSendEstimate}
                >
                  {isPendingResolve ? 'Saving…' : 'Confirm cost estimate'}
                </Button>
              </>
            ) : (
              <>
                <CannotSendRadio />
                <Button
                  className="w-full"
                  disabled={isPendingResolve}
                  onClick={handleMarkCannotSend}
                >
                  {isPendingResolve ? 'Saving…' : 'Mark complete'}
                </Button>
              </>
            )}
          </ResolutionSection>
        </>
      ) : null}
    </div>
  )
}
